import React, { useState, useRef, forwardRef, useImperativeHandle, useContext, createContext, useEffect } from "react";
import clsx from "clsx";
import tinycolor from "tinycolor2";
import Tooltip from "../Tooltip/Tooltip"; // ajuste o caminho se necessário
/**
 * ColorPicker é um componente React altamente customizável para seleção de cores, 
 * suportando entrada manual (HEX/RGB), paletas rápidas, ajuste de alpha/transparência, 
 * feedback de contraste, cópia para área de transferência e reset. 
 * Permite sobrescrever estilos via contexto ou props.
 *
 * @component
 * @example
 * // Uso básico
 * <ColorPicker
 *   label="Cor do fundo"
 *   value={color}
 *   onChange={setColor}
 * />
 *
 * @example
 * // Com paleta customizada e alpha
 * <ColorPicker
 *   label="Cor"
 *   value={color}
 *   onChange={setColor}
 *   palette={["#fff", "#000", "#f00", "#0f0", "#00f"]}
 *   advanced
 * />
 *
 * @example
 * // Com grupos de paleta e estilos customizados via Provider
 * <ColorPickerStyleProvider value={{ previewSize: 40, borderColor: "#222" }}>
 *   <ColorPicker
 *     label="Cor"
 *     value={color}
 *     onChange={setColor}
 *     paletteGroups={[
 *       { label: "Básicas", colors: ["#fff", "#000"] },
 *       { label: "Vivas", colors: ["#f00", "#0f0", "#00f"] }
 *     ]}
 *   />
 * </ColorPickerStyleProvider>
 *
 * @example
 * // Com campo de texto desabilitado e apenas preview
 * <ColorPicker
 *   value={color}
 *   onChange={setColor}
 *   showInputField={false}
 *   showPreview
 * />
 *
 * @param {ColorPickerProps} props
 * @param {string} [props.id] - ID do campo.
 * @param {string} [props.name] - Nome do campo.
 * @param {string} [props.label] - Rótulo exibido acima do campo.
 * @param {string} props.value - Valor da cor (HEX, RGB, etc).
 * @param {(color: string) => void} props.onChange - Callback ao alterar a cor.
 * @param {boolean} [props.disabled] - Desabilita o campo.
 * @param {boolean} [props.required] - Campo obrigatório.
 * @param {string} [props.helperText] - Texto de ajuda.
 * @param {boolean} [props.error] - Indica erro.
 * @param {string} [props.errorMessage] - Mensagem de erro.
 * @param {"light"|"dark"} [props.theme] - Tema visual.
 * @param {string} [props.className] - Classe extra para o container.
 * @param {string} [props.inputClassName] - Classe extra para o input.
 * @param {boolean} [props.showPreview] - Exibe preview da cor.
 * @param {string[]} [props.palette] - Paleta rápida de cores.
 * @param {{label: string, colors: string[]}[]} [props.paletteGroups] - Grupos de paleta.
 * @param {boolean} [props.autoFocus] - Autofoco no input.
 * @param {number} [props.tabIndex] - TabIndex do container.
 * @param {boolean} [props.showInputField] - Exibe campo de texto manual.
 * @param {boolean} [props.showCopy] - Exibe botão de copiar.
 * @param {boolean} [props.showReset] - Exibe botão de reset.
 * @param {string} [props.defaultValue] - Valor padrão para reset.
 * @param {() => void} [props.onFocus] - Callback ao focar.
 * @param {() => void} [props.onBlur] - Callback ao desfocar.
 * @param {boolean} [props.advanced] - Exibe controles avançados (alpha, copiar, reset, paleta).
 * @param {React.ReactNode} [props.children] - Elementos customizados.
 * @param {number} [props.previewSize] - Tamanho do preview (px).
 * @param {string} [props.borderColor] - Cor da borda padrão.
 * @param {string} [props.borderColorFocused] - Cor da borda ao focar.
 * @param {string} [props.borderColorError] - Cor da borda em erro.
 * @param {string} [props.borderColorSelected] - Cor da borda ao selecionar na paleta.
 * @param {number} [props.paletteButtonSize] - Tamanho dos botões da paleta (px).
 *
 * @returns {JSX.Element}
 */

// 0. Interface e contexto para i18n
export interface ColorPickerI18n {
  copy?: string;
  copied?: string;
  reset?: string;
  clear?: string;
  alpha?: string;
  lowContrast?: string;
  colorPickerTooltip?: string;
  required?: string;
  invalidColor?: string;
  noColor?: string;
  selectColor?: (color: string) => string;
}
const defaultI18n: Required<ColorPickerI18n> = {
  copy: "Copiar",
  copied: "Copiado!",
  reset: "Resetar",
  clear: "Limpar cor",
  alpha: "Alpha",
  lowContrast: "Baixo contraste",
  colorPickerTooltip: "Selecione uma cor ou digite um valor hexadecimal (ex: #FF5733)",
  required: "*",
  invalidColor: "Formato de cor inválido",
  noColor: "Nenhuma",
  selectColor: (color) => `Selecionar cor ${color}`,
};
const ColorPickerI18nContext = createContext<ColorPickerI18n>(defaultI18n);

// 1. Contexto para estilos globais do ColorPicker
export interface ColorPickerStyleContextProps {
  previewSize?: number;
  borderColor?: string;
  borderColorFocused?: string;
  borderColorError?: string;
  borderColorSelected?: string;
  paletteButtonSize?: number;
}
const ColorPickerStyleContext = createContext<ColorPickerStyleContextProps>({});

// 2. Atualize as props para permitir sobrescrever estilos
export interface ColorPickerProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  theme?: "light" | "dark";
  className?: string;
  inputClassName?: string;
  showPreview?: boolean;
  palette?: string[];
  paletteGroups?: { label: string; colors: string[] }[];
  autoFocus?: boolean;
  tabIndex?: number;
  showInputField?: boolean;
  showCopy?: boolean;
  showReset?: boolean;
  defaultValue?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  advanced?: boolean; // NOVA PROP
  children?: React.ReactNode; // Permite ações customizadas
  previewSize?: number;
  borderColor?: string;
  borderColorFocused?: string;
  borderColorError?: string;
  borderColorSelected?: string;
  paletteButtonSize?: number;
  i18n?: ColorPickerI18n;
  showNoneOption?: boolean; // NOVA PROP
}

export type ColorPickerRef = HTMLInputElement;

const defaultPalette = [
  "#000000", "#ffffff", "#ff0000", "#00ff00", "#0000ff",
  "#ffff00", "#ff00ff", "#00ffff", "#f59e42", "#8e44ad"
];

// Atualize normalizeColor para sempre retornar HEX (mesmo com alpha: HEX8)
function normalizeColor(value: string) {
  const tc = tinycolor(value.trim());
  if (!tc.isValid()) return value.trim();
  // Sempre retorna HEX (HEX8 se alpha < 1)
  return tc.getAlpha() < 1 ? tc.toHex8String() : tc.toHexString();
}

function isValidColor(value: string) {
  return tinycolor(value.trim()).isValid();
}

// Adicione esta função utilitária para atualizar o alpha de uma cor
function setAlpha(color: string, alpha: number) {
  const tc = tinycolor(color.trim());
  if (!tc.isValid()) return color;
  tc.setAlpha(alpha);
  return tc.getAlpha() < 1 ? tc.toRgbString() : tc.toHexString();
}

const ColorPicker = forwardRef<ColorPickerRef, ColorPickerProps>((props, ref) => {
  const {
    id,
    name,
    label,
    value,
    onChange,
    disabled = false,
    required = false,
    helperText,
    error = false,
    errorMessage,
    theme = "light",
    className,
    inputClassName,
    showPreview = true,
    palette = defaultPalette,
    paletteGroups,
    autoFocus,
    tabIndex,
    showInputField = true,
    showCopy = true,
    showReset = false,
    defaultValue,
    onFocus,
    onBlur,
    advanced = true,
    children, // Adicione aqui
    i18n: i18nProp,
    showNoneOption = false, // NOVA PROP
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [inputError, setInputError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false); // Novo estado para feedback

  const inputRef = useRef<HTMLInputElement>(null);

  // Defina normalizedValue como a cor normalizada do valor atual
  const normalizedValue = normalizeColor(value);

  useImperativeHandle(ref, () => inputRef.current!);

  // Copiar cor (sempre HEX)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(normalizedValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch {
      // Intentionally ignored
    }
  };

  // Resetar cor
  const handleReset = () => {
    if (defaultValue) onChange(defaultValue);
  };

  // Input manual de cor
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (isValidColor(val)) {
      setInputError(null);
      onChange(normalizeColor(val)); // sempre HEX
    } else {
      setInputError(i18n.invalidColor); // Usando string do i18n
    }
  };

  // Valor do alpha atual (0 a 1)
  const currentAlpha = (() => {
    const tc = tinycolor(value);
    return tc.isValid() ? tc.getAlpha() : 1;
  })();

  // Handler para mudar o alpha
  const handleAlphaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const alpha = Number(e.target.value) / 100;
    onChange(setAlpha(value, alpha));
  };

  // Preview border para contraste

  const showAlphaSlider =
    (() => {
      const tc = tinycolor(value);
      return (
        (tc.getFormat() === "rgb" || tc.getFormat() === "prgb" || tc.getFormat() === "rgba" ||
          tc.getFormat() === "hsl" || tc.getFormat() === "hsla") ||
        tc.getAlpha() < 1
      );
    })();

  // Classes...
  // (Mesma lógica das suas classes originais...)

  // Função utilitária para saber se está "sem cor"
  const isNone = !value || value.trim() === "";

  // 1. Pegue textos do contexto e sobrescreva com prop se fornecido
  const i18nContext = useContext(ColorPickerI18nContext);
  const i18n = { ...defaultI18n, ...i18nContext, ...i18nProp };

  // 3. Pegue estilos do contexto e sobrescreva com props se fornecidas
  const styleContext = useContext(ColorPickerStyleContext);
  const previewSize = props.previewSize ?? styleContext.previewSize ?? 32;
  const borderColor = props.borderColor ?? styleContext.borderColor ??
    (theme === "dark" ? "#33384d" : "#c8c6c4");
  const borderColorFocused = props.borderColorFocused ?? styleContext.borderColorFocused ??
    (theme === "dark" ? "#7b5cff" : "#0078d4");
  const borderColorError = props.borderColorError ?? styleContext.borderColorError ??
    (theme === "dark" ? "#ff7675" : "#d13438");
  const borderColorSelected = props.borderColorSelected ?? styleContext.borderColorSelected ?? "#7b5cff";
  const paletteButtonSize = props.paletteButtonSize ?? styleContext.paletteButtonSize ?? 24;

  // 4. Use os valores customizados nos estilos inline ou classes
  // Exemplo para o preview:
  const previewBorder = error
    ? `border-[${borderColorError}]`
    : isFocused
      ? `border-[${borderColorFocused}]`
      : `border-[${borderColor}]`;

  // Checkerboard SVG para fundo "sem cor"
  const Checkerboard = (
    <span
      className="block w-full h-full rounded-full"
      aria-hidden="true" // <-- acessibilidade
      style={{
        background: `
          linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%),
          linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)`,
        backgroundSize: "8px 8px",
        backgroundPosition: "0 0, 4px 4px",
      }}
    />
  );

  // Sincronize input manual se value externo mudar
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Novo: Estado para controlar se o painel está aberto (sempre true para "sempre visível")
  const [panelOpen] = useState(true);

  // Novo: Ref para o container do trigger
  const triggerRef = useRef<HTMLDivElement>(null);

  // Novo: Estilo para o painel flutuante
  const panelStyle: React.CSSProperties = {
    position: "absolute",
    zIndex: 9999,
    top: 48, // ajuste conforme necessário
    left: 0,
    minWidth: 260,
    background: theme === "dark" ? "#23242a" : "#fff",
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
    padding: 16,
  };

  return (
    <div
      className={clsx("relative w-fit", className)}
      ref={triggerRef}
      tabIndex={tabIndex} // <-- aqui!
    >
      {/* Label acima do input */}
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            "block mb-1 text-xs font-medium",
            disabled ? "text-gray-400" : "text-gray-700"
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-1">{i18n.required}</span>}
        </label>
      )}
      {/* Trigger: input + botão */}
      {showInputField && (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            id={id}
            name={name || id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            disabled={disabled}
            autoFocus={autoFocus}
            onFocus={() => {
              setIsFocused(true);
              onFocus && onFocus();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur && onBlur();
            }}
            className={clsx(
              "w-24 px-2 py-1 rounded border text-xs font-mono font-grotesk",
              (error || inputError)
                ? "border-red-400 bg-red-50 text-red-700"
                : "border-gray-300 bg-white",
              inputClassName
            )}
            aria-label="Digite a cor em HEX"
            aria-invalid={!!inputError}
            aria-describedby={inputError ? `${id}-input-error` : undefined}
            placeholder={currentAlpha < 1 ? "#RRGGBBAA" : "#RRGGBB"}
            maxLength={9}
          />
          <Tooltip tooltip={i18n.colorPickerTooltip}>
            <button
              type="button"
              className="w-8 h-8 rounded-full border border-gray-300"
              style={{ background: value || "#fff" }}
              aria-label={i18n.selectColor(value)}
              tabIndex={0}
            />
          </Tooltip>
        </div>
      )}
      {/* Helper text ou mensagem de erro */}
      {error && errorMessage ? (
        <div className="mt-1 text-xs text-red-600">{errorMessage}</div>
      ) : helperText && (
        <div className="mt-1 text-xs text-gray-500">{helperText}</div>
      )}
      {/* Painel flutuante do ColorPicker */}
      {panelOpen && (
        <div style={panelStyle}>
          {/* Preview da cor */}
          {showPreview && (
            <div
              className={clsx(
                "mb-3 flex items-center justify-center",
                previewBorder // Use a variável aqui!
              )}
              style={{
                width: previewSize,
                height: previewSize,
                borderRadius: "50%",
                background: isNone ? "transparent" : value,
                position: "relative",
                overflow: "hidden",
              }}
              aria-label={isNone ? i18n.noColor : value}
            >
              {isNone ? Checkerboard : null}
            </div>
          )}
          {/* Controles avançados */}
          {advanced && (
            <>
              {/* Opção "Nenhuma cor" */}
              {showNoneOption && (
                <button
                  type="button"
                  className={clsx(
                    "rounded-full border border-dashed border-gray-400 flex items-center justify-center",
                    value === "" || value == null ? `ring-2 ring-[${borderColorSelected}]` : ""
                  )}
                  style={{
                    width: paletteButtonSize,
                    height: paletteButtonSize,
                    background: "#fff",
                  }}
                  aria-label={i18n.noColor}
                  onClick={() => onChange("")}
                  tabIndex={0}
                  disabled={disabled}
                >
                  <span className="text-xs text-gray-500">{i18n.noColor}</span>
                </button>
              )}
              {/* Paleta rápida de cores (simples) */}
              {(!paletteGroups || paletteGroups.length === 0) && palette && palette.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {palette.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={clsx(
                        "rounded-full border",
                        value === color
                          ? `ring-2 ring-[${borderColorSelected}] border-[${borderColorSelected}]`
                          : `border-[${borderColor}]`
                      )}
                      style={{
                        width: paletteButtonSize,
                        height: paletteButtonSize,
                        background: color,
                      }}
                      aria-label={i18n.selectColor(color)}
                      onClick={() => onChange(color)}
                      tabIndex={0}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}
              {/* Paleta em grupos */}
              {paletteGroups && paletteGroups.length > 0 && (
                <div className="mb-2 flex flex-col gap-2">
                  {paletteGroups.map((group) => (
                    <div key={group.label}>
                      <div className="text-xs font-medium mb-1">{group.label}</div>
                      <div className="flex flex-wrap gap-2">
                        {group.colors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={clsx(
                              "rounded-full border",
                              value === color
                                ? `ring-2 ring-[${borderColorSelected}] border-[${borderColorSelected}]`
                                : `border-[${borderColor}]`
                            )}
                            style={{
                              width: paletteButtonSize,
                              height: paletteButtonSize,
                              background: color,
                            }}
                            aria-label={i18n.selectColor(color)}
                            onClick={() => onChange(color)}
                            tabIndex={0}
                            disabled={disabled}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Botão de copiar */}
              {showCopy && (
                <button
                  type="button"
                  className="mt-2 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-xs border border-gray-300"
                  onClick={handleCopy}
                  disabled={disabled}
                  aria-label={i18n.copy}
                >
                  {copied ? i18n.copied : i18n.copy}
                </button>
              )}
              {/* Botão de reset */}
              {showReset && (
                <button
                  type="button"
                  className="mt-2 ml-2 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-xs border border-gray-300"
                  onClick={handleReset}
                  disabled={disabled}
                  aria-label={i18n.reset}
                >
                  {i18n.reset}
                </button>
              )}
              {/* Slider de alpha */}
              {advanced && showAlphaSlider && (
                <div className="mt-3">
                  <label className="block text-xs mb-1">{i18n.alpha}</label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round(currentAlpha * 100)}
                    onChange={handleAlphaChange}
                    disabled={disabled}
                    className="w-full"
                  />
                </div>
              )}
              {/* Renderiza children customizados */}
              {children}
            </>
          )}
          {/* ...restante do conteúdo do painel... */}
        </div>
      )}
    </div>
  );
});

// 6. Exporte o Provider para i18n
export const ColorPickerI18nProvider = ColorPickerI18nContext.Provider;

// 5. Exporte o Provider para uso global
export const ColorPickerStyleProvider = ColorPickerStyleContext.Provider;

// 7. Exporte tipos utilitários e contexto para uso externo
export {
  ColorPickerI18nContext,
  ColorPickerStyleContext,
  normalizeColor,
  isValidColor,
  setAlpha,
};

ColorPicker.displayName = "ColorPicker";
export default ColorPicker;